import { useEffect, useState } from 'react';
import {
  Button,
  DataTable,
  Drawer,
  EmptyState,
  Icon,
  Stat,
  Tag,
  useErrorDialog,
  useToast,
  type Column,
} from '@dayon/design-system';
import { formatCount, formatPesos, formatPesosCompact, formatRate } from '@dayon/money';

import { api, ApiRequestError } from '../../lib/api.ts';
import {
  CHARGEBACK_CEILING_BP,
  PORTAL_STATUS_LABEL,
  PORTAL_STATUS_TONE,
  STATUS_LABEL,
  STATUS_TONE,
  describeModel,
  formatDay,
  shareBp,
  type AffiliateDetail,
  type AffiliateRow,
  type ClawbackRow,
  type LinkRow,
  type PayoutRow,
  type PeriodRow,
} from './types.ts';

/**
 * One affiliate, in full.
 *
 * Four sections that used to be empty states with a paragraph each, and are now
 * the rows the schema always held:
 *
 *   Tracking links — `affiliate.tracking_link` joined to `affiliate.link_click`,
 *     with the full URL assembled server-side from `platform.brand.domain`. The
 *     drawer used to print the affiliate code and explain that the domain was
 *     never sent to it.
 *   Player quality — retention, average first deposit and the never-funded
 *     share, each with the denominator it is a share OF. Chargeback rate is
 *     still absent and still says so, because no table records a dispute.
 *   Payout history — `affiliate.affiliate_payout` and `affiliate_clawback`,
 *     which is what lets accrued and paid finally be different numbers.
 *   Period ledger — `affiliate_period_result`, so a payout can be traced to the
 *     period it settled.
 *
 * The list row is passed in and the rest is fetched when the drawer opens, so a
 * table of six affiliates does not make twenty-four queries to render.
 */

const linkColumns: Column<LinkRow>[] = [
  {
    key: 'sub',
    header: 'Sub-ID',
    render: (l) => (
      <>
        <span className="link-url">{l.sub_id}</span>
        <div className="text-subtle" style={{ fontSize: '11px' }}>
          {l.channel}
          {l.campaign === null ? '' : ` · ${l.campaign}`}
          {l.active ? '' : ' · inactive'}
        </div>
      </>
    ),
  },
  {
    key: 'clicks',
    header: 'Clicks 30d',
    numeric: true,
    render: (l) => (
      <>
        <span className="tabular">{formatCount(Number(l.clicks_30d))}</span>
        <div className="text-subtle" style={{ fontSize: '11px' }}>
          {formatCount(Number(l.clicks))} lifetime
        </div>
      </>
    ),
  },
  {
    key: 'signups',
    header: 'Signups',
    numeric: true,
    render: (l) => <span className="tabular">{formatCount(Number(l.signups))}</span>,
  },
  {
    key: 'ftd',
    header: 'FTDs',
    numeric: true,
    render: (l) => (
      <>
        <span className="tabular">{formatCount(Number(l.ftd))}</span>
        {/* Per link, so a placement that recruits and one that converts can be
            told apart — which is the whole point of a sub-ID. */}
        {(() => {
          const bp = shareBp(l.ftd, l.signups);
          return bp === null ? null : (
            <div className="text-subtle" style={{ fontSize: '11px' }}>
              {formatRate(bp)} of signups
            </div>
          );
        })()}
      </>
    ),
  },
];

const payoutColumns: Column<PayoutRow>[] = [
  {
    key: 'ref',
    header: 'Payout',
    render: (p) => (
      <>
        {p.ref}
        <div className="text-subtle" style={{ fontSize: '11px' }}>
          {p.period_ref ?? (p.is_early ? 'early — no period' : 'no period')}
        </div>
      </>
    ),
  },
  {
    key: 'amount',
    header: 'Amount',
    numeric: true,
    render: (p) => (
      <>
        <span className="tabular">{formatPesos(BigInt(p.amount))}</span>
        {BigInt(p.handling_fee) > 0n && (
          <div className="text-subtle" style={{ fontSize: '11px' }}>
            less {formatPesos(BigInt(p.handling_fee))} fee
          </div>
        )}
      </>
    ),
  },
  {
    key: 'status',
    header: 'Status',
    render: (p) => (
      <>
        <Tag tone={p.status === 'PAID' ? 'accent' : 'outline'}>{p.status.toLowerCase()}</Tag>
        {p.is_early && (
          <div className="text-subtle" style={{ fontSize: '11px' }}>
            early payout
          </div>
        )}
      </>
    ),
  },
  {
    key: 'who',
    header: 'Raised · approved',
    secondary: true,
    render: (p) => (
      <>
        {p.initiated_by ?? 'the affiliate'}
        <div className="text-subtle" style={{ fontSize: '11px' }}>
          {/* Not "—": an unapproved payout is one waiting for a checker, and
              that is the state an operator has to act on. */}
          {p.approved_by === null ? 'not approved yet' : `approved by ${p.approved_by}`}
        </div>
      </>
    ),
  },
  { key: 'paid', header: 'Paid', secondary: true, render: (p) => formatDay(p.paid_at) },
];

const clawbackColumns: Column<ClawbackRow>[] = [
  { key: 'when', header: 'Raised', render: (c) => formatDay(c.created_at) },
  {
    key: 'amount',
    header: 'Amount',
    numeric: true,
    render: (c) => <span className="tabular">−{formatPesos(BigInt(c.amount))}</span>,
  },
  {
    key: 'reason',
    header: 'Reason',
    render: (c) => (
      <>
        {c.reason_code}
        {c.note === null ? null : (
          <div className="text-subtle" style={{ fontSize: '11px' }}>
            {c.note}
          </div>
        )}
      </>
    ),
  },
];

const periodColumns: Column<PeriodRow>[] = [
  {
    key: 'period',
    header: 'Period',
    render: (p) => (
      <>
        {p.ref}
        <div className="text-subtle" style={{ fontSize: '11px' }}>
          {formatDay(p.period_start)} – {formatDay(p.period_end)}
        </div>
      </>
    ),
  },
  {
    key: 'net',
    header: 'Net GGR',
    numeric: true,
    render: (p) => <span className="tabular">{formatPesos(BigInt(p.net_revenue))}</span>,
  },
  {
    key: 'commission',
    header: 'Commission',
    numeric: true,
    render: (p) => (
      <>
        <span className="tabular">{formatPesos(BigInt(p.net_commission))}</span>
        {BigInt(p.cpa_commission) > 0n && (
          <div className="text-subtle" style={{ fontSize: '11px' }}>
            incl. {formatPesos(BigInt(p.cpa_commission))} CPA
          </div>
        )}
      </>
    ),
  },
  {
    key: 'status',
    header: 'Status',
    render: (p) => (
      <>
        <Tag tone={p.status === 'PAID' ? 'accent' : 'outline'}>{p.status.toLowerCase()}</Tag>
        {p.held && (
          <div className="text-subtle" style={{ fontSize: '11px' }}>
            held
          </div>
        )}
      </>
    ),
  },
];

export function AffiliateDetailDrawer({
  affiliate,
  floorBp,
  canWrite,
  onClose,
  onChangePlan,
  onCloseReview,
  onPortalAccess,
}: {
  affiliate: AffiliateRow | null;
  floorBp: number;
  canWrite: boolean;
  onClose: () => void;
  onChangePlan: (row: AffiliateRow) => void;
  onCloseReview: (row: AffiliateRow) => void;
  /** Opens the portal-access dialog. Reading the state needs no capability. */
  onPortalAccess: (row: AffiliateRow) => void;
}) {
  const { showError } = useErrorDialog();
  const { toast } = useToast();
  const [detail, setDetail] = useState<AffiliateDetail | null>(null);
  const [loading, setLoading] = useState(false);
  /* Its own flag, like the ledger's. A detail read that failed must not print
     "no tracking links" over an affiliate that has four. */
  const [failed, setFailed] = useState(false);

  const id = affiliate?.id ?? null;

  useEffect(() => {
    if (id === null) {
      setDetail(null);
      setFailed(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setDetail(null);
    setFailed(false);

    api
      .get<AffiliateDetail>(`/api/admin/affiliates/${id}/detail`)
      .then((payload) => {
        if (cancelled) return;
        setDetail(payload);
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        setFailed(true);
        showError({
          title: 'Could not load the affiliate detail',
          message:
            error instanceof ApiRequestError
              ? error.message
              : 'The links, payouts and periods could not be fetched. Try again shortly.',
        });
      })
      .finally(() => !cancelled && setLoading(false));

    return () => {
      cancelled = true;
    };
  }, [id, showError]);

  /* Nothing is rendered at all when there is no affiliate. A closed <Drawer />
     would still mount its focus-trap effects for a record that does not exist,
     and the whole body below dereferences `affiliate`. */
  if (affiliate === null) return null;

  /** The full link for the affiliate's first sub-ID, or the code if there is none. */
  const primaryLink = detail?.links[0] ?? null;

  const copyLink = () => {
    const text =
      primaryLink === null ? affiliate.ref : `https://${primaryLink.url}`;
    navigator.clipboard
      .writeText(text)
      .then(() =>
        toast(
          primaryLink === null
            ? `Affiliate code copied · ${affiliate.ref} (this affiliate has no tracking link)`
            : `Tracking link copied · ${primaryLink.sub_id}`,
        ),
      )
      .catch(() =>
        showError({
          title: 'Could not copy',
          message: `The browser refused clipboard access. The value is ${text}.`,
        }),
      );
  };

  const retentionBp = shareBp(affiliate.retained_30d, affiliate.ftd_players);
  const bonusOnlyBp = shareBp(affiliate.bonus_only, affiliate.referred_players);

  return (
    <Drawer
      open
      onClose={onClose}
      title={affiliate.name}
      subtitle={
        <span className="row">
          <Tag tone={STATUS_TONE[affiliate.status] ?? 'neutral'}>
            {STATUS_LABEL[affiliate.status] ?? affiliate.status}
          </Tag>
          <span>
            {affiliate.ref} · {affiliate.source_kind} ·{' '}
            {describeModel(affiliate.model, affiliate.revshare_bp, affiliate.cpa_amount)}
          </span>
        </span>
      }
      actions={
        <>
          <Button variant="ghost" onClick={copyLink}>
            <Icon name="copy" />
            {primaryLink === null ? 'Copy code' : 'Copy link'}
          </Button>
          {canWrite && (
            <Button variant="ghost" onClick={() => onChangePlan(affiliate)}>
              <Icon name="receipt" />
              Commission model
            </Button>
          )}
          {/* Offered whatever the state, and to a reader as well as a writer:
              the dialog is where "this affiliate cannot sign in at all" is
              said, and that is the fact worth reaching even when the operator
              cannot act on it themselves. */}
          <Button variant="ghost" onClick={() => onPortalAccess(affiliate)}>
            <Icon name="key" />
            Portal access
          </Button>
          {canWrite && affiliate.status === 'REVIEW' && (
            <Button variant="primary" onClick={() => onCloseReview(affiliate)}>
              Close review
            </Button>
          )}
        </>
      }
    >
      <div className="stack">
        <div className="stat-grid">
          <Stat
            label="Signups"
            value={formatCount(Number(affiliate.signups_30d))}
            delta={`30 days · ${formatCount(Number(affiliate.signups))} lifetime`}
          />
          <Stat
            label="FTDs"
            value={formatCount(Number(affiliate.ftd_30d))}
            delta={
              affiliate.conversion_bp_30d === null
                ? 'no signups in the window to convert'
                : `${formatRate(Number(affiliate.conversion_bp_30d))} conversion`
            }
          />
          <Stat
            label="Accrued commission"
            value={formatPesosCompact(BigInt(affiliate.accrued_commission))}
            delta="unsettled periods only"
          />
          <Stat
            label="Paid"
            value={formatPesosCompact(BigInt(affiliate.paid_commission))}
            delta={
              affiliate.last_paid_at === null
                ? 'never paid'
                : `last ${formatDay(affiliate.last_paid_at)}`
            }
          />
        </div>

        {/* ── Can they sign in? ─────────────────────────────────────────
            The question this screen could not answer at all until the API grew
            a way to say yes. It sits above the money because a partner who
            cannot see a statement will ask a person for one, and the operator
            reading this drawer is that person. */}
        <div className="notice notice-quiet">
          <Icon name="key" />
          <div>
            <Tag tone={PORTAL_STATUS_TONE[affiliate.portal_status] ?? 'quiet'}>
              {PORTAL_STATUS_LABEL[affiliate.portal_status] ?? affiliate.portal_status}
            </Tag>{' '}
            {affiliate.portal_status === 'NONE' ? (
              <>
                Nobody at {affiliate.name} can sign in to the affiliate portal — there is no login
                for it. <strong>Portal access</strong> above creates one.
              </>
            ) : affiliate.portal_status === 'INVITED' ? (
              <>
                {affiliate.portal_email} was invited and has not accepted, so they still cannot
                sign in. <strong>Portal access</strong> above re-issues the link.
              </>
            ) : (
              <>{affiliate.portal_email} signs in to the affiliate portal.</>
            )}
          </div>
        </div>

        {BigInt(affiliate.clawed_back) > 0n && (
          <div className="notice">
            <Icon name="warning-circle" />
            <div>
              {formatPesos(BigInt(affiliate.clawed_back))} has been clawed back from this
              affiliate. Clawbacks are not netted off the accrued figure above — they are their own
              rows, listed below.
            </div>
          </div>
        )}

        {/* ── Tracking links ────────────────────────────────────────────── */}
        <h4 className="panel-title" style={{ margin: 0 }}>
          Tracking links and sub-IDs
        </h4>
        {loading ? (
          <div className="skeleton" style={{ height: '120px' }}>
            &nbsp;
          </div>
        ) : (
          <>
            <DataTable
              caption="Tracking links for this affiliate, with clicks, signups and first deposits per sub-ID"
              columns={linkColumns}
              rows={detail?.links ?? []}
              rowKey={(l) => l.id}
              empty={
                <EmptyState
                  icon={failed ? 'warning-circle' : 'hash'}
                  title={failed ? 'The links could not be read' : 'No tracking link'}
                >
                  {/* Two sentences for two opposite facts, and the operator acts
                      differently on each: an affiliate with no link needs one
                      issued, while a drawer that could not read them needs
                      reopening. Whichever is shown, the rows behind it come from
                      `affiliate.tracking_link` — the name belongs here and not on
                      the screen, because an operator cannot look a table up, ask
                      anyone for it, or do anything at all with the word, whereas
                      whoever edits this file needs to know which rows are missing. */}
                  {failed
                    ? 'The detail request did not come back, so no link is listed because none was read — not because this affiliate has none.'
                    : 'No tracking link is recorded for this affiliate, so it has nothing to send traffic to. Nothing was filtered out — there is genuinely none stored. A link is issued whenever an affiliate is filed, so this one was filed before that became the practice.'}
                </EmptyState>
              }
            />
            {primaryLink !== null && (
              <div className="notice notice-quiet">
                <Icon name="link-simple" />
                <div>
                  <code>https://{primaryLink.url}</code> — built from the brand&rsquo;s own domain
                  and the affiliate code. Copy takes the whole link, not just the code.
                </div>
              </div>
            )}
          </>
        )}

        {/* ── Player quality ────────────────────────────────────────────── */}
        <h4 className="panel-title" style={{ margin: 0 }}>
          Player quality
        </h4>
        <dl className="stack" style={{ margin: 0 }}>
          <div className="def-row">
            <dt>FTD conversion, 30 days (floor {formatRate(floorBp)})</dt>
            <dd>
              {affiliate.conversion_bp_30d === null
                ? '— no signups in the window'
                : formatRate(Number(affiliate.conversion_bp_30d))}
            </dd>
          </div>
          <div className="def-row">
            <dt>Average first deposit, 30 days</dt>
            <dd>
              {/* Null is "nobody made a first deposit in the window", which is
                  a different fact from an average of nothing. */}
              {affiliate.avg_first_deposit === null
                ? '— no first deposit in the window'
                : formatPesos(BigInt(affiliate.avg_first_deposit))}
            </dd>
          </div>
          <div className="def-row">
            <dt>30-day retention</dt>
            <dd>
              {retentionBp === null ? (
                '— no depositing players yet'
              ) : (
                <>
                  {formatRate(retentionBp)}
                  <span className="text-subtle">
                    {' '}
                    · {formatCount(Number(affiliate.retained_30d))} of{' '}
                    {formatCount(Number(affiliate.ftd_players))} depositing players played
                  </span>
                </>
              )}
            </dd>
          </div>
          <div className="def-row">
            <dt>Bonus-only share</dt>
            <dd>
              {bonusOnlyBp === null ? (
                '— no referred players'
              ) : (
                <>
                  {formatRate(bonusOnlyBp)}
                  <span className="text-subtle">
                    {' '}
                    · {formatCount(Number(affiliate.bonus_only))} of{' '}
                    {formatCount(Number(affiliate.referred_players))} took a bonus and never
                    deposited
                  </span>
                </>
              )}
            </dd>
          </div>
          <div className="def-row">
            <dt>Chargeback rate</dt>
            {/* Not a dash on its own, which reads as "none". */}
            <dd className="text-subtle">not measured — no chargeback rate is computed</dd>
          </div>
          <div className="def-row">
            <dt>Clicks, 30 days</dt>
            <dd>
              {formatCount(Number(affiliate.clicks_30d))}
              <span className="text-subtle">
                {' '}
                · {formatCount(Number(affiliate.clicks))} lifetime across{' '}
                {formatCount(Number(affiliate.links))} links
              </span>
            </dd>
          </div>
          <div className="def-row">
            <dt>Quality review opened</dt>
            <dd>
              {affiliate.quality_review_opened_at === null
                ? 'never'
                : formatDay(affiliate.quality_review_opened_at)}
            </dd>
          </div>
          <div className="def-row">
            <dt>Filed · approved</dt>
            <dd>
              {affiliate.created_by === null ? 'not recorded' : 'recorded'} ·{' '}
              {affiliate.approved_at === null ? 'not approved' : formatDay(affiliate.approved_at)}
            </dd>
          </div>
        </dl>
        <div className="notice notice-quiet">
          <Icon name="info" />
          <div>
            Retention is depositing players who placed a settled bet in the last 30 gaming days,
            over all depositing players — not a share of signups. Bonus-only is players who took a
            bonus and have no settled deposit. Chargeback rate is not measured: no payment provider
            reports chargebacks or disputes to the platform, and nothing turns the chargebacks staff
            record by hand into a rate, so the{' '}
            <em>chargebacks above {formatRate(CHARGEBACK_CEILING_BP)}</em> half of the auto-review
            rule cannot be evaluated — only the FTD-conversion half can.
          </div>
        </div>

        {/* ── Settlement ────────────────────────────────────────────────── */}
        <h4 className="panel-title" style={{ margin: 0 }}>
          Payouts
        </h4>
        {loading ? (
          <div className="skeleton" style={{ height: '100px' }}>
            &nbsp;
          </div>
        ) : (
          <DataTable
            caption="Payouts to this affiliate, with who raised and who approved each"
            columns={payoutColumns}
            rows={detail?.payouts ?? []}
            rowKey={(p) => p.ref}
            empty={
              <EmptyState
                icon={failed ? 'warning-circle' : 'calendar-blank'}
                title={failed ? 'The payouts could not be read' : 'Nothing has been paid'}
              >
                {/* The accrued figure is quoted in the empty case on purpose: an
                    affiliate that has never been paid is the one whose balance
                    matters most, and an empty table on its own reads as "nothing
                    owing" when it means the opposite. The rows counted here are
                    `affiliate.affiliate_payout`; the name stays in this comment,
                    where it helps whoever maintains the drawer, rather than on
                    the screen, where it tells an operator nothing they can use. */}
                {failed
                  ? 'The detail request did not come back, so no payout is listed because none was read.'
                  : `No payment to this affiliate has ever been recorded — nothing was filtered out, there is genuinely nothing. Its ${formatPesos(BigInt(affiliate.accrued_commission))} of accrued commission is all still owed.`}
              </EmptyState>
            }
          />
        )}

        {(detail?.clawbacks.length ?? 0) > 0 && (
          <>
            <h4 className="panel-title" style={{ margin: 0 }}>
              Clawbacks
            </h4>
            <DataTable
              caption="Commission reversed from this affiliate"
              columns={clawbackColumns}
              rows={detail?.clawbacks ?? []}
              rowKey={(c) => `${c.created_at}:${c.amount}:${c.reason_code}`}
            />
          </>
        )}

        <h4 className="panel-title" style={{ margin: 0 }}>
          Period ledger
        </h4>
        {loading ? (
          <div className="skeleton" style={{ height: '100px' }}>
            &nbsp;
          </div>
        ) : (
          <DataTable
            caption="Commission periods for this affiliate, with net revenue, commission and settlement status"
            columns={periodColumns}
            rows={detail?.periods ?? []}
            rowKey={(p) => p.ref}
            empty={
              <EmptyState
                icon={failed ? 'warning-circle' : 'receipt'}
                title={failed ? 'The periods could not be read' : 'No commission period'}
              >
                {/* "No period has been worked out" is the fact an operator needs,
                    because a payout can only be traced back to a settled period
                    and there is nothing here to trace one to yet. The rows are
                    `affiliate.affiliate_period_result`, recorded here rather than
                    on the screen for the same reason as the two tables above: the
                    name is an identifier the schema happens to use, and the person
                    reading the drawer can do nothing with it. */}
                {failed
                  ? 'The detail request did not come back, so no period is listed because none was read.'
                  : 'No commission period has ever been worked out for this affiliate, so there is nothing yet for a payout to be traced back to. Nothing was filtered out — there is genuinely nothing recorded.'}
              </EmptyState>
            }
          />
        )}
      </div>
    </Drawer>
  );
}
